/**
 * ProtectedRoute - Auth guard component with approval status check
 *
 * Features:
 * - Redirects unauthenticated users to login
 * - Shows PendingApprovalScreen for users awaiting admin approval
 * - Shows RejectedScreen for rejected users
 * - Renders children for approved users
 */

import { ReactNode, useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import PendingApprovalScreen from './PendingApprovalScreen';
import RejectedScreen from './RejectedScreen';

interface ProtectedRouteProps {
  children: ReactNode;
}

type ApprovalStatus = 'pending' | 'approved' | 'rejected' | null;

export default function ProtectedRoute({ children }: ProtectedRouteProps) {
  const { user, loading } = useAuth();
  const [approvalStatus, setApprovalStatus] = useState<ApprovalStatus>(null);
  const [checkingApproval, setCheckingApproval] = useState(true);

  // Check approval status when user changes
  useEffect(() => {
    const checkApprovalStatus = async () => {
      if (!user) {
        setCheckingApproval(false);
        setApprovalStatus(null);
        return;
      }

      try {
        const { data, error } = await supabase
          .from('profiles')
          .select('approval_status')
          .eq('user_id', user.id)
          .single();

        if (error) {
          // Profile might not exist yet - treat as pending
          console.log('[ProtectedRoute] Profile not found, treating as pending');
          setApprovalStatus('pending');
        } else if (data) {
          setApprovalStatus(data.approval_status as ApprovalStatus);
        } else {
          // No data - treat as pending
          setApprovalStatus('pending');
        }
      } catch (err) {
        console.error('[ProtectedRoute] Error checking approval status:', err);
        // On error, allow access (fail open for existing users)
        setApprovalStatus('approved');
      } finally {
        setCheckingApproval(false);
      }
    };

    checkApprovalStatus();
  }, [user?.id]);

  // Show loading spinner while checking auth or approval
  if (loading || checkingApproval) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-orca-elevate"></div>
          <p className="mt-4 text-steel">Loading...</p>
        </div>
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!user) {
    console.log('[ProtectedRoute] No user found, redirecting to login...');
    return <Navigate to="/login" replace />;
  }

  // Show pending approval screen
  if (approvalStatus === 'pending') {
    return <PendingApprovalScreen />;
  }

  // Show rejected screen
  if (approvalStatus === 'rejected') {
    return <RejectedScreen />;
  }

  // Render protected content for approved users
  return <>{children}</>;
}
